import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { getProjectModuleCapabilities } from '../../config/projectCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getProjectDetails } from '../../services/projectService';
import type { ProjectDetails } from '../../types/project';
import { projectErrorMessage } from '../../utils/projectErrors';
import { useTaskFocusTimer } from '../../hooks/useTaskFocusTimer';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { TaskDetailsPanel } from './TaskDetailsPanel';

type Props = NativeStackScreenProps<RootStackParamList, 'ProjectTaskDetails'>;

export function ProjectTaskDetailsScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const capabilities = getProjectModuleCapabilities(auth.permissions, auth.roles);
  const projectId = route.params.projectId;
  const taskId = route.params.taskId;
  // Bumped when a focus session is logged so the task reloads its time.
  const [timeLogVersion, setTimeLogVersion] = useState(0);
  const { active: activeTimer, start: startTimer, stop: stopTimer } =
    useTaskFocusTimer({
      projectId,
      onSessionLogged: () => setTimeLogVersion(current => current + 1),
    });
  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProject(await getProjectDetails(projectId));
      setError('');
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to load the task.'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const themed = useMemo(
    () => ({ loading: { ...typography.body, color: palette.textSecondary } }),
    [palette],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.PROJECT_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen} backgroundColor={palette.background}>
        {loading && !project ? (
          <View style={styles.center}>
            <Text style={themed.loading}>Loading task…</Text>
          </View>
        ) : error && !project ? (
          <ErrorState message={error} onRetry={() => load()} />
        ) : !project ? (
          <ErrorState message="Task not found." onRetry={() => load()} />
        ) : (
          <TaskDetailsPanel
            projectId={projectId}
            taskId={taskId}
            members={project.members}
            columns={project.columns}
            canEdit={capabilities.canManageTasks}
            onClose={() => navigation.goBack()}
            activeTimer={activeTimer}
            onStartTimer={mode => startTimer(taskId, mode)}
            onStopTimer={stopTimer}
            timeLogVersion={timeLogVersion}
          />
        )}
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0, justifyContent: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
